import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, ApiError, query } from "../api/client";
import { useQuery } from "../api/useQuery";
import { useAuth } from "../auth/AuthContext";
import { fmtDateTime, fmtTime, incidentStatusName, priorityName, roleName } from "../components/format";
import { Photo, PhotoThumb } from "../components/Photo";
import type { Incident, IncidentAction, IncidentStatus, Page } from "../api/types";

const SORTS: [string, string][] = [
  ["priority", "Сначала срочные"],
  ["received", "По времени получения"],
  ["created", "По времени фиксации"],
  ["category", "По категории"],
  ["territory", "По территории"],
];

const PRIORITIES: [string, string][] = [
  ["", "Любая срочность"],
  ["2", "Срочно и высокий"],
  ["1", "Только срочно"],
];

const PAGE_SIZE = 20;

const TABS: [string, string][] = [
  ["new", "Новые"],
  ["in_progress", "В работе"],
  ["closed", "Закрытые"],
  ["", "Все"],
];

// Разрешённые переходы повторяют серверные: сервер всё равно проверит,
// но предлагать заведомо невозможное действие не нужно.
const TRANSITIONS: Record<IncidentStatus, IncidentStatus[]> = {
  new: ["seen", "in_progress", "rejected"],
  seen: ["in_progress", "forwarded", "closed", "rejected"],
  in_progress: ["forwarded", "closed", "rejected"],
  forwarded: ["closed", "rejected"],
  closed: [],
  rejected: [],
};

const ACTION_NAME: Record<IncidentStatus, string> = {
  new: "Новое",
  seen: "Отметить просмотренным",
  in_progress: "Взять в работу",
  forwarded: "Передать",
  closed: "Закрыть",
  rejected: "Отклонить",
};

/** Лента инцидентов: список слева, карточка выбранного справа. */
export function IncidentsPage() {
  const { me } = useAuth();
  // Переход из уведомления открывает конкретный инцидент.
  const [params] = useSearchParams();
  const [status, setStatus] = useState(params.get("id") ? "" : "new");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("");
  const [sort, setSort] = useState("priority");
  const [offset, setOffset] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(params.get("id"));
  const categories = useQuery<{ items: { code: string; name: string }[] }>("/api/v1/incident-categories");

  const path = "/api/v1/incidents" + query({
    status, category, max_priority: priority, sort, limit: PAGE_SIZE, offset,
  });
  const { data, error, loading, reload } = useQuery<Page<Incident>>(path);

  const items = data?.items ?? [];
  const selected = items.find((i) => i.id === selectedId) ?? items[0] ?? null;
  const canHandle = me?.role !== "inspector";
  const reset = (fn: () => void) => { fn(); setOffset(0); setSelectedId(null); };

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Инциденты</h2>
          {data && <p className="muted small">{data.total} в этом разделе</p>}
        </div>
        <div className="segmented" role="group" aria-label="Статус">
          {TABS.map(([value, title]) => (
            <button key={value} aria-pressed={status === value}
              onClick={() => { setStatus(value); setOffset(0); setSelectedId(null); }}>
              {title}
            </button>
          ))}
        </div>
      </div>

      <div className="filters">
        <label className="field">Категория
          <select value={category} onChange={(e) => reset(() => setCategory(e.target.value))}>
            <option value="">Все категории</option>
            {categories.data?.items.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </label>
        <label className="field">Срочность
          <select value={priority} onChange={(e) => reset(() => setPriority(e.target.value))}>
            {PRIORITIES.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        </label>
        <label className="field">Порядок
          <select value={sort} onChange={(e) => reset(() => setSort(e.target.value))}>
            {SORTS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        </label>
      </div>

      {error && <div className="error" role="alert">{error}</div>}
      {loading && !data && <p className="notice">Загрузка…</p>}
      {data && items.length === 0 && <div className="card empty">Здесь пусто.</div>}

      {items.length > 0 && (
        <div className="split">
          <div className="split-list">
            <div className="list">
              {items.map((inc) => (
                <button key={inc.id} className={`item${inc.id === selected?.id ? " selected" : ""}`}
                  onClick={() => setSelectedId(inc.id)}>
                  <PhotoThumb id={inc.photos.find((p) => p.state === "complete")?.id ?? null} />
                  <span className="item-body">
                    <span className="item-head">
                      <span className="item-title">{inc.category_name}</span>
                      <span className="small muted">{fmtWhen(inc.device_time)}</span>
                    </span>
                    <span className="item-text">{inc.description || "Без описания"}</span>
                    <span className="small muted">
                      <PriorityBadge priority={inc.priority} /> {inc.author_name} · {inc.territory_name}
                    </span>
                  </span>
                </button>
              ))}
            </div>
            {data && data.total > PAGE_SIZE && (
              <div className="filters" style={{ marginTop: 12 }}>
                <button className="secondary" disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Назад</button>
                <span className="muted small">
                  {offset + 1}–{Math.min(offset + PAGE_SIZE, data.total)} из {data.total}
                </span>
                <button className="secondary" disabled={offset + PAGE_SIZE >= data.total}
                  onClick={() => setOffset(offset + PAGE_SIZE)}>Далее</button>
              </div>
            )}
          </div>

          {selected && (
            <section className="split-detail card" aria-label="Карточка инцидента">
              <div className="page-head" style={{ marginBottom: 16 }}>
                <div>
                  <div className="muted small">{fmtDateTime(selected.device_time)}</div>
                  <h3 style={{ fontSize: 22, margin: "2px 0 0" }}>{selected.category_name}</h3>
                </div>
              </div>
              {canHandle && <StatusActions key={selected.id} incident={selected} onDone={reload} />}

              {selected.photos.length > 0 && (
                <div className="photos" style={{ marginBottom: 16 }}>
                  {selected.photos.map((p) =>
                    p.state === "complete"
                      ? <Photo key={p.id} id={p.id} />
                      : <div key={p.id} className="map-empty small">Фото ещё догружается с устройства</div>,
                  )}
                </div>
              )}

              {selected.description && <p style={{ margin: "0 0 16px", fontSize: 15 }}>{selected.description}</p>}

              <div className="list" style={{ background: "var(--bg)" }}>
                <div className="row"><span className="muted">Статус</span><span>{incidentStatusName[selected.status]}</span></div>
                <div className="row"><span className="muted">Срочность</span><span>{priorityName(selected.priority)}</span></div>
                <div className="row"><span className="muted">Инспектор</span><span>{selected.author_name}</span></div>
                <div className="row"><span className="muted">Территория</span><span>{selected.territory_name}</span></div>
                <div className="row">
                  <span className="muted">Координаты</span>
                  <span>
                    {selected.lat !== null && selected.lon !== null
                      ? `${selected.lat.toFixed(5)}, ${selected.lon.toFixed(5)}` +
                        (selected.accuracy_m !== null ? ` · ±${Math.round(selected.accuracy_m)} м` : "")
                      : "не было спутников"}
                  </span>
                </div>
                {/* Разрыв между фиксацией и получением показывает, сколько
                    инспектор был вне связи. Скрывать его нельзя. */}
                {new Date(selected.received_at).getTime() - new Date(selected.device_time).getTime() > 300000 && (
                  <div className="row"><span className="muted">Получено</span><span>{fmtDateTime(selected.received_at)}</span></div>
                )}
                {selected.patrol_id && (
                  <div className="row"><span className="muted">Обход</span><Link to={`/patrols/${selected.patrol_id}`}>Открыть →</Link></div>
                )}
                <div className="row">
                  <span className="muted">Ответственный</span>
                  {canHandle
                    ? <AssigneeSelect key={selected.id} incident={selected} onDone={reload} />
                    : <span>{selected.assignee_name ?? "не назначен"}</span>}
                </div>
              </div>

              <IncidentHistory key={`h-${selected.id}-${selected.status}-${selected.assignee_id}`} incident={selected} canComment={canHandle} />
            </section>
          )}
        </div>
      )}
    </>
  );
}

function PriorityBadge({ priority }: { priority: number }) {
  return <span className={`badge${priority === 1 ? " danger" : ""}`}>{priorityName(priority)}</span>;
}

/** Сегодняшнее — временем, остальное — датой. */
function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return d.toDateString() === new Date().toDateString() ? fmtTime(iso) : (fmtDateTime(iso).split(",")[0] ?? iso);
}

/** Смена статуса с комментарием; отклонение — только с причиной. */
function StatusActions({ incident, onDone }: { incident: Incident; onDone: () => void }) {
  const [next, setNext] = useState<IncidentStatus | null>(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const options = TRANSITIONS[incident.status];
  if (options.length === 0) return null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!next) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/incidents/${incident.id}/status`, { status: next, comment: comment.trim() });
      setNext(null);
      setComment("");
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось изменить статус");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="filters" style={{ margin: 0 }}>
        {options.map((s, i) => (
          <button key={s} disabled={busy} aria-pressed={next === s}
            className={s === "rejected" ? "danger" : i === 0 ? "" : "secondary"}
            onClick={() => { setNext(next === s ? null : s); setError(null); }}>
            {ACTION_NAME[s]}
          </button>
        ))}
      </div>
      {next && (
        <form onSubmit={(e) => void submit(e)} className="correction-form">
          {error && <div className="error" role="alert">{error}</div>}
          <label className="field">{next === "rejected" ? "Причина отклонения" : "Комментарий (необязательно)"}
            <textarea rows={2} value={comment} required={next === "rejected"} onChange={(e) => setComment(e.target.value)} />
          </label>
          <div className="filters" style={{ margin: 0 }}>
            <button type="submit" className={next === "rejected" ? "danger" : undefined}
              disabled={busy || (next === "rejected" && !comment.trim())}>
              {ACTION_NAME[next]}
            </button>
            <button type="button" className="secondary" onClick={() => setNext(null)}>Отмена</button>
          </div>
        </form>
      )}
    </div>
  );
}

function AssigneeSelect({ incident, onDone }: { incident: Incident; onDone: () => void }) {
  const candidates = useQuery<{ items: { id: string; full_name: string; role: string }[] }>(
    `/api/v1/incidents/${incident.id}/assignees`);
  const [error, setError] = useState<string | null>(null);
  async function assign(id: string) {
    setError(null);
    try {
      await api.post(`/api/v1/incidents/${incident.id}/assignee`, { assignee_id: id });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Не удалось назначить");
    }
  }
  return (
    <span>
      <select value={incident.assignee_id ?? ""} onChange={(e) => e.target.value && void assign(e.target.value)}>
        <option value="">не назначен</option>
        {candidates.data?.items.map((c) => (
          <option key={c.id} value={c.id}>{c.full_name} — {roleName[c.role] ?? c.role}</option>
        ))}
      </select>
      {error && <span className="error small"> {error}</span>}
    </span>
  );
}

/** Журнал действий: статусы, назначения и комментарии одной лентой. */
function IncidentHistory({ incident, canComment }: { incident: Incident; canComment: boolean }) {
  const history = useQuery<{ items: IncidentAction[] }>(`/api/v1/incidents/${incident.id}/history`);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function comment(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/incidents/${incident.id}/comments`, { text });
      setText("");
      history.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось добавить комментарий");
    } finally {
      setBusy(false);
    }
  }

  const items = history.data?.items ?? [];
  return (
    <div style={{ marginTop: 20 }}>
      <div className="section-title">История обработки</div>
      {items.length === 0 && history.data && <p className="muted small">Пока никто не работал с этим сообщением.</p>}
      <ul className="plain">
        {items.map((a, i) => (
          <li key={i}>
            <div className="small muted">{fmtDateTime(a.at)} · {a.actor}</div>
            <div>
              {a.kind === "status" && <>Статус: <strong>{incidentStatusName[a.to_status ?? "new"]}</strong></>}
              {a.kind === "assign" && <>Назначен ответственный: <strong>{a.assignee}</strong></>}
              {a.kind === "comment" && a.text}
            </div>
            {a.kind === "status" && a.text && <div className="small">{a.text}</div>}
          </li>
        ))}
      </ul>
      {canComment && (
        <form onSubmit={(e) => void comment(e)} className="correction-form">
          {error && <div className="error" role="alert">{error}</div>}
          <label className="field">Комментарий
            <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} />
          </label>
          <div className="filters" style={{ margin: 0 }}>
            <button type="submit" disabled={busy || !text.trim()}>Добавить комментарий</button>
          </div>
        </form>
      )}
    </div>
  );
}
